import type { Job } from '@/types'

export type JobStatusEntityLabels = {
  entity?: string
  entityLower?: string
  entityPluralLower?: string
  workDayPluralLower?: string
}

const DEFAULT_JOB_STATUS_ENTITY_LABELS: Required<JobStatusEntityLabels> = {
  entity: 'Job',
  entityLower: 'job',
  entityPluralLower: 'jobs',
  workDayPluralLower: 'work days',
}

function resolveJobStatusEntityLabels(labels?: JobStatusEntityLabels) {
  return { ...DEFAULT_JOB_STATUS_ENTITY_LABELS, ...labels }
}

export type JobStatus = Job['status']

export type BackendJobStatus =
  | 'draft'
  | 'scheduled'
  | 'assigned'
  | 'in_progress'
  | 'withdrawn'
  | 'completed'
  | 'cancelled'
  | 'on_hold'

type JobStatusRecord = Pick<Job, 'status' | 'is_locked'> & Partial<Pick<Job, 'workflow_status_code' | 'workflow_status_label' | 'has_invoice'>>

const TERMINAL_JOB_STATUSES = new Set<string>(['withdrawn', 'completed', 'cancelled'])

export const JOB_STATUS_LABELS: Record<JobStatus, string> = {
  draft: 'Draft',
  open: 'Open',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
  on_hold: 'On Hold',
}

const WORKFLOW_JOB_STATUS_LABELS: Record<string, string> = {
  ...JOB_STATUS_LABELS,
  scheduled: 'Scheduled',
  assigned: 'Assigned',
}

export const JOB_STATUS_FORM_OPTIONS = (
  Object.entries(JOB_STATUS_LABELS) as Array<[JobStatus, string]>
).map(([value, label]) => ({ value, label }))

export function getJobWorkflowStatusCode(job?: Partial<Pick<Job, 'status' | 'workflow_status_code'>> | null): string | null {
  const workflowStatusCode = String(job?.workflow_status_code ?? '').trim()
  return workflowStatusCode || null
}

function getJobBlockingStatusCode(job?: Partial<Pick<Job, 'status' | 'workflow_status_code'>> | null): string | null {
  const workflowStatusCode = getJobWorkflowStatusCode(job)
  if (workflowStatusCode) {
    return workflowStatusCode
  }

  const status = String(job?.status ?? '').trim()
  return status || null
}

export function normalizeBackendJobStatus(status?: string | null): JobStatus {
  const value = String(status ?? '').trim()

  if (value === 'scheduled' || value === 'assigned') {
    return 'open'
  }

  if (value in JOB_STATUS_LABELS) {
    return value as JobStatus
  }

  return 'draft'
}

export function toBackendJobStatus(status?: string | null): BackendJobStatus | undefined {
  switch (status) {
    case 'open':
      return 'scheduled'
    case 'draft':
    case 'in_progress':
    case 'withdrawn':
    case 'completed':
    case 'cancelled':
    case 'on_hold':
      return status
    default:
      return undefined
  }
}

export function getJobStatusLabel(status?: string | null): string {
  const value = String(status ?? '').trim()
  if (value && value in WORKFLOW_JOB_STATUS_LABELS) {
    return WORKFLOW_JOB_STATUS_LABELS[value]
  }

  if (!value) {
    return 'Unknown'
  }

  return value.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
}

export function getJobStatusBadgeClassName(status?: string | null): string {
  const classes: Record<string, string> = {
    draft: 'border-slate-200 bg-slate-50 text-slate-700',
    open: 'border-blue-200 bg-blue-50 text-blue-700',
    scheduled: 'border-blue-200 bg-blue-50 text-blue-700',
    assigned: 'border-indigo-200 bg-indigo-50 text-indigo-700',
    in_progress: 'border-amber-200 bg-amber-50 text-amber-700',
    withdrawn: 'border-rose-200 bg-rose-50 text-rose-700',
    completed: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    cancelled: 'border-rose-200 bg-rose-50 text-rose-700',
    on_hold: 'border-orange-200 bg-orange-50 text-orange-700',
  }

  const normalized = String(status ?? 'draft').trim()
  return classes[normalized] ?? classes.draft
}

export function isTerminalJobStatus(status?: string | null): boolean {
  return TERMINAL_JOB_STATUSES.has(String(status ?? '').trim())
}

export function isWorkdayMutationBlockedJobStatus(status?: string | null): boolean {
  return isTerminalJobStatus(status)
}

export function isJobLocked(job?: Pick<Job, 'is_locked'> | null): boolean {
  return Boolean(job?.is_locked)
}

export function isJobModificationBlocked(
  job?: JobStatusRecord | null,
): boolean {
  if (!job) {
    return false
  }

  return isJobLocked(job) || isTerminalJobStatus(getJobBlockingStatusCode(job))
}

export function getJobModificationBlockedMessage(
  job?: JobStatusRecord | null,
  entityLabels?: JobStatusEntityLabels,
): string | null {
  if (!isJobModificationBlocked(job)) {
    return null
  }

  const labels = resolveJobStatusEntityLabels(entityLabels)

  if (isJobLocked(job)) {
    return `This ${labels.entityLower} is locked and cannot be modified.`
  }

  return `Withdrawn, completed, or cancelled ${labels.entityPluralLower} cannot be modified.`
}

export function getJobEditBlockedMessage(
  job?: JobStatusRecord | null,
  entityLabels?: JobStatusEntityLabels,
): string | null {
  if (!job) {
    return null
  }

  const labels = resolveJobStatusEntityLabels(entityLabels)

  if (job.has_invoice) {
    return 'Edit is unavailable after invoice is issued.'
  }

  if (isJobLocked(job)) {
    return `Unlock the ${labels.entityLower} to edit details.`
  }

  return getJobModificationBlockedMessage(job, entityLabels)
}

export function getJobStatusChangeBlockedMessage(
  job?: JobStatusRecord | null,
  entityLabels?: JobStatusEntityLabels,
): string | null {
  if (!job) {
    return null
  }

  const labels = resolveJobStatusEntityLabels(entityLabels)

  if (job.has_invoice) {
    return 'Status cannot be changed after invoice is issued.'
  }

  if (isJobLocked(job)) {
    return `Status cannot be changed while the ${labels.entityLower} is locked.`
  }

  if (isTerminalJobStatus(getJobBlockingStatusCode(job))) {
    return `Status cannot be changed for withdrawn, completed, or cancelled ${labels.entityPluralLower}.`
  }

  return null
}

function buildWorkdayBlockedMessage(
  job: JobStatusRecord,
  {
    lockedMessage,
    terminalMessage,
  }: {
    lockedMessage: string
    terminalMessage: (statusLabel: string) => string
  },
): string | null {
  if (isJobLocked(job)) {
    return lockedMessage
  }

  const statusCode = getJobBlockingStatusCode(job)
  if (isTerminalJobStatus(statusCode)) {
    return terminalMessage(job.workflow_status_label || getJobStatusLabel(statusCode))
  }

  return null
}

export function getJobScheduleBlockedMessage(
  job?: JobStatusRecord | null,
  entityLabels?: JobStatusEntityLabels,
): string | null {
  if (!job) {
    return null
  }

  const labels = resolveJobStatusEntityLabels(entityLabels)

  return buildWorkdayBlockedMessage(job, {
    lockedMessage: `${labels.workDayPluralLower} and entries cannot be changed while this ${labels.entityLower} is locked.`,
    terminalMessage: (statusLabel) =>
      `${labels.workDayPluralLower} and entries cannot be changed while this ${labels.entityLower} is ${statusLabel}.`,
  })
}

export function getWorkdayEntryJobBlockedMessage(
  job?: JobStatusRecord | null,
  entityLabels?: JobStatusEntityLabels,
): string | null {
  if (!job) {
    return null
  }

  const labels = resolveJobStatusEntityLabels(entityLabels)

  return buildWorkdayBlockedMessage(job, {
    lockedMessage: `Work day entries cannot be changed while this ${labels.entityLower} is locked.`,
    terminalMessage: (statusLabel) =>
      `Work day entries cannot be changed while this ${labels.entityLower} is ${statusLabel}.`,
  })
}

export function getWorkdayEntryStatusChangeBlockedMessage(
  job?: JobStatusRecord | null,
  entityLabels?: JobStatusEntityLabels,
): string | null {
  if (!job) {
    return null
  }

  const labels = resolveJobStatusEntityLabels(entityLabels)

  return buildWorkdayBlockedMessage(job, {
    lockedMessage: `Work day entry status cannot be changed while this ${labels.entityLower} is locked.`,
    terminalMessage: (statusLabel) =>
      `Work day entry status cannot be changed while this ${labels.entityLower} is ${statusLabel}.`,
  })
}

export function getWorkdayCreationJobBlockedMessage(
  job?: JobStatusRecord | null,
  entityLabels?: JobStatusEntityLabels,
): string | null {
  if (!job) {
    return null
  }

  const labels = resolveJobStatusEntityLabels(entityLabels)

  return buildWorkdayBlockedMessage(job, {
    lockedMessage: `${labels.workDayPluralLower} cannot be created while this ${labels.entityLower} is locked.`,
    terminalMessage: (statusLabel) =>
      `${labels.workDayPluralLower} cannot be created while this ${labels.entityLower} is ${statusLabel}.`,
  })
}

export function getWorkdayMutationJobBlockedMessage(
  job?: JobStatusRecord | null,
  entityLabels?: JobStatusEntityLabels,
): string | null {
  if (!job) {
    return null
  }

  const labels = resolveJobStatusEntityLabels(entityLabels)

  return buildWorkdayBlockedMessage(job, {
    lockedMessage: `${labels.workDayPluralLower} cannot be changed while this ${labels.entityLower} is locked.`,
    terminalMessage: (statusLabel) =>
      `${labels.workDayPluralLower} cannot be changed while this ${labels.entityLower} is ${statusLabel}.`,
  })
}
